import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CoverageSchemaResponse } from '@/features/quotes/types/group-life/coverage-schema.types';
import type { CoverageSelection as ResolverCoverageSelection } from '@/features/quotes/utils/group-life/coverage-resolver';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { isCpfAlreadyRegistered } from '@/shared/utils';
import { useSearch } from '@tanstack/react-router';

export type CoverageSelection = ResolverCoverageSelection;

/**
 * Local draft state of a Group Life PME quote.
 *
 * This layer exists while the InsureMO Group Policy integration (Phase 3)
 * is not implemented yet. Everything here is kept in memory / wizard state.
 * When integrated, this context will be replaced by real loading from
 * InsureMO via React Query.
 */

export type GroupLifeStipulator = {
  insuranceType: '1' | '3' | '4';
  isCaepf: boolean;
  registerNumber: string;
  /** CAEPF: CPF consultado (o `registerNumber` traz o CAEPF). Vazio p/ CNPJ. */
  cpf?: string;
  responsibleName: string;
  cnaeCode: string;
  email: string;
  phone: string;
  insurerCompanyId?: string;
  /** Idades médias da Diretrix (quote-engine-data) — persistidas no
   *  PolicyCustomer como AveragePartnerAge/AverageEmployeeAge. */
  averagePartnerAge?: number;
  averageEmployeeAge?: number;
};

export type Modality = 'UNIFORM' | 'VARIABLE' | 'MULTIPLE_SALARY';

export type GroupLifePlan = {
  modality: Modality;
  /** Capital single value (only for Uniform modality). */
  uniformCapital?: string;
  /** Number of salaries (only for Multiple Salary modality). */
  numberOfSalaries?: string;
  /** Minimum individual capital (Variable + Multiple Salary). */
  minimumIndividualCapital?: string;
  /** Maximum individual capital (Variable + Multiple Salary). */
  maximumIndividualCapital?: string;
  /** Personal Health Statement (DPS) — Maximum waiver capital (informational). */
  maximumDpsWaiverCapital?: string;
  /** Map coverageCode → selection. */
  coverages: Record<string, CoverageSelection>;
};

export type GroupLifeMember = {
  id: string; // client-side uuid; replaced by InsureMO id in Phase 3
  cpf: string;
  name: string;
  dateOfBirth: string; // DD/MM/YYYY
  gender: 'M' | 'F';
  insurableGroup: string;
  capital: string;
  awayFromWork: boolean;
  cids: string[]; // up to 5 ICD-10 (CID-10) codes
};

export type GroupLifeSubStipulator = {
  id: string; // client-side uuid
  cnpj: string;
  legalName: string;
  cnaeCode?: string;
  email?: string;
  phone?: string;
  /** true → documento é CAEPF (pessoa física); false/ausente → CNPJ. */
  isCaepf?: boolean;
  /** CAEPF: CPF consultado (o `cnpj` carrega o CAEPF). */
  cpf?: string;
  /** CAEPF (pessoa física): sexo e data de nascimento do titular (proposta). */
  genderCode?: string;
  dateOfBirth?: string;
  members: GroupLifeMember[];
};

export type GroupLifeCoBroker = {
  agentCode: string;
  name: string;
  /** Participação formatada pt-BR, ex.: "30,00". */
  sharePercent: string;
  registerNumber?: string;
  susepCode?: string;
};

export type GroupLifeCommercialization = {
  /** Brokerage percentage (formatted %). */
  brokeragePercent: string;
  /** Agencing percentage (formatted %). */
  agencingPercent: string;
  /** Agency percentage (formatted %). */
  agencyPercent: string;
  /** Agravo — PolicyDiscountLoadingList tipo "S" (string pt-BR "10,00"). */
  surchargePercent?: string;
  /** Desconto atuarial — PolicyDiscountLoadingList tipo "D". */
  actuarialDiscountPercent?: string;
  /** Participação do líder. Vazio sem cocorretagem — a tela mostra 100%. */
  leaderSharePercent?: string;
  coBrokers?: GroupLifeCoBroker[];
};

export type GroupLifeCalculation = {
  netPremium: number;
  iof: number;
  totalPremium: number;
  contractType: string;
  quoteNumber: string;
  validity: string;
  frequency: string;
  /** Premium breakdown by coverage / line. */
  breakdown: {
    description: string;
    value: number;
    iof: number;
    total: number;
  }[];
  /** Timestamp of last calculation. */
  calculatedAt: number;
};

export type GroupLifeQuoteState = {
  stipulator?: GroupLifeStipulator;
  /**
   * Members directly linked to the main Stipulator (Step 1).
   * In the UI the Stipulator is treated as the first member group,
   * alongside the SubStipulators.
   */
  stipulatorMembers: GroupLifeMember[];
  plan?: GroupLifePlan;
  subStipulators: GroupLifeSubStipulator[];
  commercialization?: GroupLifeCommercialization;
  calculation?: GroupLifeCalculation;
};

type ContextValue = GroupLifeQuoteState & {
  /**
   * Schema do produto técnico carregado pelo hook `useGroupLifeCoverageSchema`.
   * Mantido no contexto para que o resolver e a lógica de exclusão mútua
   * tenham acesso sem precisar re-buscar.
   */
  coverageSchema?: CoverageSchemaResponse;
  setCoverageSchema: (schema: CoverageSchemaResponse | undefined) => void;
  /** Atualiza uma cobertura individual aplicando regras de exclusão mútua. */
  updateCoverage: (code: string, patch: CoverageSelection) => void;
  setStipulator: (data: GroupLifeStipulator) => void;
  setPlan: (data: GroupLifePlan) => void;
  /**
   * Adiciona um subestipulante no contexto local. O `id` é opcional — quando
   * informado (caso comum: resposta do back após `POST /sub-stipulators`), é
   * usado tal-qual; senão, um uuid client-side é atribuído (legado / cenário
   * offline).
   */
  addSubStipulator: (
    data: Omit<GroupLifeSubStipulator, 'members'> | Omit<GroupLifeSubStipulator, 'id' | 'members'>,
  ) => void;
  /** Atualiza os campos básicos (CNPJ, razão social, etc.) de um sub. */
  updateSubStipulator: (
    id: string,
    data: Omit<GroupLifeSubStipulator, 'id' | 'members'>,
  ) => void;
  removeSubStipulator: (id: string) => void;
  addMemberToSubStipulator: (subId: string, data: Omit<GroupLifeMember, 'id'>) => void;
  removeMemberFromSubStipulator: (subId: string, memberId: string) => void;
  bulkAddMembersToSubStipulator: (
    subId: string,
    list: (Omit<GroupLifeMember, 'id'> & { id?: string })[],
  ) => void;
  /**
   * Substitui INTEGRALMENTE as vidas de um sub específico pelas informadas
   * (outros subs e o estipulante master não são tocados). Usado no
   * COMPLETED do upload async para evitar duplicação preview+hidratado.
   */
  replaceMembersInSubStipulator: (
    subId: string,
    list: (Omit<GroupLifeMember, 'id'> & { id?: string })[],
  ) => void;
  addMemberToStipulator: (data: Omit<GroupLifeMember, 'id'>) => void;
  removeMemberFromStipulator: (memberId: string) => void;
  bulkAddMembersToStipulator: (
    list: (Omit<GroupLifeMember, 'id'> & { id?: string })[],
  ) => void;
  updateMemberInStipulator: (
    memberId: string,
    data: Omit<GroupLifeMember, 'id'>,
  ) => void;
  updateMemberInSubStipulator: (
    subId: string,
    memberId: string,
    data: Omit<GroupLifeMember, 'id'>,
  ) => void;
  /**
   * Remove TODAS as vidas (estipulante + todos os subestipulantes). Apenas
   * para facilitar testes locais — não persiste no InsureMO.
   */
  clearAllMembers: () => void;
  /**
   * Verifica se um CPF já está cadastrado em qualquer vida da cotação
   * (estipulante OU qualquer subestipulante). CPFs vazios nunca são
   * considerados duplicados (vidas não-nomeadas / headcount). `excludeMemberId`
   * permite ignorar a própria vida ao editar.
   */
  isCpfRegistered: (cpf: string, excludeMemberId?: string) => boolean;
  setCommercialization: (data: GroupLifeCommercialization) => void;
  setCalculation: (data: GroupLifeCalculation | undefined) => void;
  resetQuote: () => void;
};

const GroupLifeQuoteContext = createContext<ContextValue | undefined>(undefined);

function makeId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Persistência local do rascunho da cotação Group Life.
 *
 * O back só persiste de forma durável Estipulante/Plano/Comercialização no
 * InsureMO (createEx/updateEx); sub-estipulantes e vidas ainda não têm
 * endpoint de PolicyRisk liberado, então se o corretor recarregar a página
 * ou reabrir a cotação esses dados se perderiam. Para evitar isso gravamos o
 * estado completo do wizard em `localStorage`, chaveado por `policyId`, e
 * restauramos no carregamento. Cobre os casos "recarregar acidentalmente" e
 * "reabrir a cotação no mesmo navegador". (Cross-device exigiria um draft no
 * back — follow-up.)
 */
const DRAFT_KEY_PREFIX = 'group-life-draft:';
const draftKey = (policyId: string) => `${DRAFT_KEY_PREFIX}${policyId}`;

function readDraft(policyId: string): GroupLifeQuoteState | null {
  try {
    const raw = localStorage.getItem(draftKey(policyId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GroupLifeQuoteState;
    // Sanidade mínima — garante os arrays obrigatórios.
    return {
      ...parsed,
      subStipulators: Array.isArray(parsed.subStipulators)
        ? parsed.subStipulators
        : [],
      stipulatorMembers: Array.isArray(parsed.stipulatorMembers)
        ? parsed.stipulatorMembers
        : [],
    };
  } catch {
    return null;
  }
}

function writeDraft(policyId: string, state: GroupLifeQuoteState): void {
  try {
    // **Vidas NÃO vão para o localStorage.** A fonte autoritativa é o
    // InsureMO (via store durável no back). O draft local persiste apenas o
    // que o back ainda não materializa em tempo real (Estipulante, Plano,
    // Comercialização, lista de Subestipulantes), para o corretor não perder
    // o trabalho num reload acidental. As vidas vêm sempre do server na
    // hidratação — refletindo o que está de fato cadastrado na apólice.
    const slim: GroupLifeQuoteState = {
      ...state,
      stipulatorMembers: [],
      subStipulators: state.subStipulators.map(s => ({ ...s, members: [] })),
    };
    localStorage.setItem(draftKey(policyId), JSON.stringify(slim));
  } catch {
    // localStorage cheio/indisponível — ignora (persistência é best-effort).
  }
}

function clearDraft(policyId: string): void {
  try {
    localStorage.removeItem(draftKey(policyId));
  } catch {
    /* noop */
  }
}

export function GroupLifeQuoteProvider({ children }: { children: ReactNode }) {
  const { policyId } = useQuoteParams();
  const search = useSearch({ strict: false }) as { quoteFlow?: string };
  const isEditFlow = search.quoteFlow === 'edit';
  const [state, setState] = useState<GroupLifeQuoteState>({
    subStipulators: [],
    stipulatorMembers: [],
  });
  const [coverageSchema, setCoverageSchemaState] = useState<
    CoverageSchemaResponse | undefined
  >();

  // ─── Restauração do rascunho local ───
  // Quando o `policyId` fica disponível (criação no step 1 ou reload com o id
  // na URL), tenta restaurar o estado salvo. Só aplica se o contexto ainda
  // estiver vazio, para não sobrescrever edições já feitas nesta sessão.
  const restoredForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!policyId) return;
    if (isEditFlow) {
      clearDraft(policyId);
      return;
    }
    if (restoredForRef.current === policyId) return;
    restoredForRef.current = policyId;
    const draft = readDraft(policyId);
    if (!draft) return;
    setState(prev => {
      const isEmpty =
        !prev.stipulator &&
        !prev.plan &&
        !prev.commercialization &&
        prev.subStipulators.length === 0 &&
        prev.stipulatorMembers.length === 0;
      return isEmpty ? draft : prev;
    });
  }, [policyId, isEditFlow]);

  // ─── Gravação (debounced) do rascunho local ───
  // Persiste todo o estado a cada mudança, independentemente de avançar ou
  // voltar etapa, para que um reload nunca perca o que já foi preenchido.
  // Em `quoteFlow=edit` a fonte é sempre o InsureMO — não gravamos rascunho.
  useEffect(() => {
    if (!policyId || isEditFlow) return;
    const handle = setTimeout(() => writeDraft(policyId, state), 400);
    return () => clearTimeout(handle);
  }, [policyId, state, isEditFlow]);

  const setStipulator = useCallback((data: GroupLifeStipulator) => {
    setState(prev => ({ ...prev, stipulator: data }));
  }, []);

  const setPlan = useCallback((data: GroupLifePlan) => {
    setState(prev => ({ ...prev, plan: data }));
  }, []);

  const setCoverageSchema = useCallback(
    (schema: CoverageSchemaResponse | undefined) => {
      setCoverageSchemaState(schema);
    },
    [],
  );

  const updateCoverage = useCallback(
    (code: string, patch: CoverageSelection) => {
      setState(prev => {
        const currentPlan = prev.plan ?? {
          modality: 'UNIFORM' as Modality,
          coverages: {},
        };
        const nextCoverages = { ...currentPlan.coverages };
        nextCoverages[code] = patch;

        // Exclusão mútua NÃO desmarca as irmãs: o conflito é acusado como erro
        // na linha pelo `resolveAll` (coverage-resolver), e o corretor decide
        // qual manter. Desmarcar por conta própria escondia a regra do usuário.

        return {
          ...prev,
          plan: { ...currentPlan, coverages: nextCoverages },
        };
      });
    },
    [],
  );

  const addSubStipulator = useCallback(
    (
      data:
        | Omit<GroupLifeSubStipulator, 'members'>
        | Omit<GroupLifeSubStipulator, 'id' | 'members'>,
    ) => {
      const incomingId = (data as { id?: string }).id;
      setState(prev => ({
        ...prev,
        subStipulators: [
          ...prev.subStipulators,
          { ...data, id: incomingId || makeId(), members: [] },
        ],
      }));
    },
    [],
  );

  const updateSubStipulator = useCallback(
    (id: string, data: Omit<GroupLifeSubStipulator, 'id' | 'members'>) => {
      setState(prev => ({
        ...prev,
        subStipulators: prev.subStipulators.map(s =>
          s.id === id ? { ...s, ...data } : s,
        ),
      }));
    },
    [],
  );

  const removeSubStipulator = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      subStipulators: prev.subStipulators.filter(s => s.id !== id),
    }));
  }, []);

  const addMemberToSubStipulator = useCallback(
    (subId: string, data: Omit<GroupLifeMember, 'id'>) => {
      setState(prev => ({
        ...prev,
        subStipulators: prev.subStipulators.map(s =>
          s.id === subId
            ? { ...s, members: [...s.members, { ...data, id: makeId() }] }
            : s,
        ),
      }));
    },
    [],
  );

  const removeMemberFromSubStipulator = useCallback(
    (subId: string, memberId: string) => {
      setState(prev => ({
        ...prev,
        subStipulators: prev.subStipulators.map(s =>
          s.id === subId
            ? { ...s, members: s.members.filter(m => m.id !== memberId) }
            : s,
        ),
      }));
    },
    [],
  );

  const bulkAddMembersToSubStipulator = useCallback(
    (subId: string, list: (Omit<GroupLifeMember, 'id'> & { id?: string })[]) => {
      setState(prev => ({
        ...prev,
        subStipulators: prev.subStipulators.map(s =>
          s.id === subId
            ? {
                ...s,
                members: [
                  ...s.members,
                  // Preserva o id vindo do back (import) — é a âncora do diff
                  // incremental no step de vidas; gera só quando não há.
                  ...list.map(m => ({ ...m, id: m.id || makeId() })),
                ],
              }
            : s,
        ),
      }));
    },
    [],
  );

  const replaceMembersInSubStipulator = useCallback(
    (subId: string, list: (Omit<GroupLifeMember, 'id'> & { id?: string })[]) => {
      setState(prev => ({
        ...prev,
        subStipulators: prev.subStipulators.map(s =>
          s.id === subId
            ? {
                ...s,
                // Substitui — não anexa. Usado no COMPLETED do upload async
                // para evitar duplicação preview (adicionado quando o usuário
                // confirma) + hidratado (vindo do back após o batch).
                members: list.map(m => ({ ...m, id: m.id || makeId() })),
              }
            : s,
        ),
      }));
    },
    [],
  );

  const addMemberToStipulator = useCallback(
    (data: Omit<GroupLifeMember, 'id'>) => {
      setState(prev => ({
        ...prev,
        stipulatorMembers: [
          ...prev.stipulatorMembers,
          { ...data, id: makeId() },
        ],
      }));
    },
    [],
  );

  const removeMemberFromStipulator = useCallback((memberId: string) => {
    setState(prev => ({
      ...prev,
      stipulatorMembers: prev.stipulatorMembers.filter(m => m.id !== memberId),
    }));
  }, []);

  const bulkAddMembersToStipulator = useCallback(
    (list: (Omit<GroupLifeMember, 'id'> & { id?: string })[]) => {
      setState(prev => ({
        ...prev,
        stipulatorMembers: [
          ...prev.stipulatorMembers,
          // Preserva o id vindo do back (import) — âncora do diff incremental.
          ...list.map(m => ({ ...m, id: m.id || makeId() })),
        ],
      }));
    },
    [],
  );

  const updateMemberInStipulator = useCallback(
    (memberId: string, data: Omit<GroupLifeMember, 'id'>) => {
      setState(prev => ({
        ...prev,
        stipulatorMembers: prev.stipulatorMembers.map(m =>
          m.id === memberId ? { ...data, id: m.id } : m,
        ),
      }));
    },
    [],
  );

  const updateMemberInSubStipulator = useCallback(
    (subId: string, memberId: string, data: Omit<GroupLifeMember, 'id'>) => {
      setState(prev => ({
        ...prev,
        subStipulators: prev.subStipulators.map(s =>
          s.id === subId
            ? {
                ...s,
                members: s.members.map(m =>
                  m.id === memberId ? { ...data, id: m.id } : m,
                ),
              }
            : s,
        ),
      }));
    },
    [],
  );

  const clearAllMembers = useCallback(() => {
    setState(prev => ({
      ...prev,
      stipulatorMembers: [],
      subStipulators: prev.subStipulators.map(s => ({ ...s, members: [] })),
    }));
  }, []);

  const isCpfRegistered = useCallback(
    (cpf: string, excludeMemberId?: string): boolean => {
      const registered = [
        ...state.stipulatorMembers,
        ...state.subStipulators.flatMap(s => s.members),
      ]
        .filter((m: GroupLifeMember) => m.id !== excludeMemberId)
        .map((m: GroupLifeMember) => m.cpf);
      return isCpfAlreadyRegistered(cpf, registered);
    },
    [state.stipulatorMembers, state.subStipulators],
  );

  const setCommercialization = useCallback(
    (data: GroupLifeCommercialization) => {
      setState(prev => ({ ...prev, commercialization: data }));
    },
    [],
  );

  const setCalculation = useCallback(
    (data: GroupLifeCalculation | undefined) => {
      setState(prev => ({ ...prev, calculation: data }));
    },
    [],
  );

  const resetQuote = useCallback(() => {
    setState({ subStipulators: [], stipulatorMembers: [] });
    if (policyId) clearDraft(policyId);
  }, [policyId]);

  const value = useMemo<ContextValue>(
    () => ({
      ...state,
      coverageSchema,
      setCoverageSchema,
      updateCoverage,
      setStipulator,
      setPlan,
      addSubStipulator,
      updateSubStipulator,
      removeSubStipulator,
      addMemberToSubStipulator,
      removeMemberFromSubStipulator,
      bulkAddMembersToSubStipulator,
      replaceMembersInSubStipulator,
      addMemberToStipulator,
      removeMemberFromStipulator,
      bulkAddMembersToStipulator,
      updateMemberInStipulator,
      updateMemberInSubStipulator,
      clearAllMembers,
      isCpfRegistered,
      setCommercialization,
      setCalculation,
      resetQuote,
    }),
    [
      state,
      coverageSchema,
      setCoverageSchema,
      updateCoverage,
      setStipulator,
      setPlan,
      addSubStipulator,
      updateSubStipulator,
      removeSubStipulator,
      addMemberToSubStipulator,
      removeMemberFromSubStipulator,
      bulkAddMembersToSubStipulator,
      replaceMembersInSubStipulator,
      addMemberToStipulator,
      removeMemberFromStipulator,
      bulkAddMembersToStipulator,
      updateMemberInStipulator,
      updateMemberInSubStipulator,
      clearAllMembers,
      isCpfRegistered,
      setCommercialization,
      setCalculation,
      resetQuote,
    ],
  );

  return (
    <GroupLifeQuoteContext.Provider value={value}>
      {children}
    </GroupLifeQuoteContext.Provider>
  );
}

export function useGroupLifeQuote(): ContextValue {
  const ctx = useContext(GroupLifeQuoteContext);
  if (!ctx) {
    throw new Error(
      'useGroupLifeQuote must be used inside GroupLifeQuoteProvider',
    );
  }
  return ctx;
}
